import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { api, ApiError } from '../api';
import type { Account, Party, Settlement, SettlementAdvisory, VarianceSeverity } from '../api';
import { Button, ErrorBanner, Spinner } from '../ui/primitives';

export function SettlementRow({
  settlement,
  accountsById,
  clientsById,
  advisories,
  editable,
  onEdited,
}: {
  settlement: Settlement;
  accountsById: Map<number, Account>;
  clientsById: Map<number, Party>;
  advisories: SettlementAdvisory[];
  editable: boolean;
  onEdited: () => void;
}) {
  const qc = useQueryClient();
  const [editing, setEditing] = useState(false);
  const [uplinePoints, setUplinePoints] = useState(settlement.uplinePoints);
  const [clientPoints, setClientPoints] = useState(settlement.clientPoints);
  const [err, setErr] = useState<string | null>(null);

  const saveMut = useMutation({
    mutationFn: () =>
      api.put<Settlement>(`/api/settlements/${settlement.id}/points-override`, {
        uplinePoints,
        clientPoints,
      }),
    onSuccess: () => {
      setErr(null);
      setEditing(false);
      void qc.invalidateQueries();
      onEdited();
    },
    onError: (e) => setErr(e instanceof ApiError ? e.message : String(e)),
  });
  const clearMut = useMutation({
    mutationFn: () =>
      api.delete<Settlement>(`/api/settlements/${settlement.id}/points-override`),
    onSuccess: () => {
      void qc.invalidateQueries();
      onEdited();
    },
  });

  const account = accountsById.get(settlement.accountId);
  const accountName = account?.canonicalName ?? `#${settlement.accountId}`;
  const panel = account?.panel;

  const isPending = settlement.state === 'pending_confirmation';
  const hasData = Number(settlement.uplinePoints ?? 0) !== 0 || Number(settlement.clientPoints ?? 0) !== 0;
  // Dim rows that have no actual points (pending with zero data)
  const rowBase = isPending && !hasData
    ? 'opacity-40'
    : isPending
    ? 'bg-amber-50/60'
    : '';

  return (
    <tr
      data-row-kind="settlement"
      data-row-id={settlement.id}
      className={`${rowBase} transition-colors hover:bg-surface-raised/60`}
    >
      {/* Account */}
      <td className="px-3 py-2.5">
        <div className="text-caption font-semibold text-ink">{accountName}</div>
        {panel && (
          <div className="mt-0.5 max-w-[14ch] truncate text-[10px] text-ink-muted">{panel}</div>
        )}
      </td>

      {/* ── CLIENT SIDE ──────────────────────────────── */}
      {/* Client pts */}
      <td className="border-l border-border/40 px-3 py-2.5 text-right">
        {editing ? (
          <input
            className="w-28 rounded border border-border bg-surface px-2 py-1 text-right text-caption font-mono focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand/30"
            value={clientPoints}
            onChange={(e) => setClientPoints(e.target.value)}
            onFocus={(e) => e.target.select()}
            inputMode="decimal"
          />
        ) : (
          <span className="font-mono text-caption tabular-nums text-ink-muted">
            {settlement.clientPoints}
            {settlement.pointsOverridden && (
              <span className="ml-1 rounded bg-sec-batches-tint px-1 py-0.5 text-[9px] text-sec-batches">
                ov
              </span>
            )}
          </span>
        )}
      </td>
      {/* C.rate */}
      <td className="px-3 py-2.5 text-right font-mono text-caption tabular-nums text-ink-muted">
        {settlement.clientRateUsed
          ? `${(Number(settlement.clientRateUsed) * 100).toFixed(1)}%`
          : '—'}
      </td>
      {/* Client amount */}
      <td className="px-3 py-2.5 text-right font-mono text-xs tabular-nums font-semibold text-pos">
        {settlement.clientAmount ?? '—'}
      </td>

      {/* ── UPLINE SIDE ──────────────────────────────── */}
      {/* Upline pts */}
      <td className="border-l border-border/40 px-3 py-2.5 text-right">
        {editing ? (
          <input
            className="w-28 rounded border border-border bg-surface px-2 py-1 text-right text-caption font-mono focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand/30"
            value={uplinePoints}
            onChange={(e) => setUplinePoints(e.target.value)}
            onFocus={(e) => e.target.select()}
            inputMode="decimal"
          />
        ) : (
          <span className="font-mono text-caption tabular-nums text-ink-muted">
            {settlement.uplinePoints}
          </span>
        )}
      </td>
      {/* U.rate */}
      <td className="px-3 py-2.5 text-right font-mono text-caption tabular-nums text-ink-muted">
        {`${(Number(settlement.uplineRateUsed) * 100).toFixed(1)}%`}
      </td>
      {/* Upline cost */}
      <td className="px-3 py-2.5 text-right font-mono text-caption tabular-nums font-semibold text-brand">
        {settlement.uplineCost}
      </td>

      {/* ── RESULT ───────────────────────────────────── */}
      {/* Margin */}
      <td className={`border-l border-border/40 px-3 py-2.5 text-right font-mono text-caption tabular-nums font-bold ${
        Number(settlement.userMargin) >= 0 ? 'text-pos' : 'text-neg'
      }`}>
        {settlement.userMargin}
      </td>
      {/* Variance */}
      <td className="px-3 py-2.5 text-center">
        <VarianceBadge severity={settlement.varianceSeverity} delta={settlement.varianceDelta} />
      </td>
      {/* Advisories */}
      <td className="px-3 py-2.5">
        <div className="flex flex-wrap gap-1">
          {advisories
            .filter((a) => a.settlementId === settlement.id)
            .map((a, i) => (
              <AdvisoryBadge key={i} kind={a.kind} detail={a.detail} />
            ))}
        </div>
      </td>
      {/* Actions — pinned right so Edit is reachable without horizontal scroll. */}
      <td className="sticky right-0 bg-surface px-3 py-2.5 text-right shadow-[-10px_0_12px_-10px_rgba(15,23,42,0.18)]">
        {editable && (
          <div className="flex items-center justify-end gap-1">
            {editing ? (
              <>
                <Button variant="ghost" onClick={() => {
                  setEditing(false);
                  setUplinePoints(settlement.uplinePoints);
                  setClientPoints(settlement.clientPoints);
                }}>
                  Cancel
                </Button>
                <Button onClick={() => saveMut.mutate()} disabled={saveMut.isPending}>
                  {saveMut.isPending ? <Spinner /> : 'Save'}
                </Button>
              </>
            ) : (
              <>
                <Button variant="ghost" onClick={() => setEditing(true)}>
                  Edit
                </Button>
                {settlement.pointsOverridden && (
                  <Button variant="ghost" onClick={() => clearMut.mutate()}>
                    Clear
                  </Button>
                )}
              </>
            )}
          </div>
        )}
      </td>
      {err && (
        <td colSpan={11}>
          <ErrorBanner>{err}</ErrorBanner>
        </td>
      )}
    </tr>
  );
}

function VarianceBadge({
  severity,
  delta,
}: {
  severity: VarianceSeverity;
  delta: string | null;
}) {
  const tones = {
    NONE: 'bg-surface-raised text-ink-muted',
    MINOR: 'bg-yellow-50 text-yellow-700 border border-yellow-200',
    REVIEW: 'bg-orange-50 text-orange-700 border border-orange-200',
    MAJOR: 'bg-red-50 text-red-700 border border-red-200',
  };
  return (
    <span
      title={delta ? `Δ = ${delta}` : undefined}
      className={[
        'inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-medium',
        tones[severity],
      ].join(' ')}
    >
      {severity}
      {delta && severity !== 'NONE' && (
        <span className="ml-1 font-mono text-[9px]">{delta}</span>
      )}
    </span>
  );
}

function AdvisoryBadge({
  kind,
  detail,
}: {
  kind: SettlementAdvisory['kind'];
  detail: string;
}) {
  const tones = {
    rotation: 'bg-sky-50 text-sky-700 border border-sky-200',
    rate_drift: 'bg-purple-50 text-purple-700 border border-purple-200',
    no_assignment: 'bg-amber-50 text-amber-700 border border-amber-200',
    missing_raw: 'bg-surface-raised text-ink-muted ring-1 ring-border',
    duplicate_raw: 'bg-red-50 text-red-700 border border-red-200',
  };
  return (
    <span
      title={detail}
      className={[
        'mr-1 inline-flex items-center rounded px-1.5 py-0.5 text-[10px] font-medium',
        tones[kind],
      ].join(' ')}
    >
      {kind.replace('_', ' ')}
    </span>
  );
}
